<template>
  <div class="ForumChatThreadCardFooter" :class="classList">
    <div class="ForumChatThreadCardFooter__grid">
      <div v-if="thread.themes.length" class="ForumChatThreadCardFooter__grid-tags">
        <div class="ForumChatThreadCardFooter__tags-container">
          <LLTags
            v-if="thread.themes.length"
            :with-remove="false"
            :with-input="false"
            :values="thread.themes"
            overflow-hidden
            :overflow-lines="compact ? 1 : 4"
          ></LLTags>
        </div>
      </div>
      <div class="ForumChatThreadCardFooter__grid-counters">
        <div class="ForumChatThreadCardFooter__counters">
          <div class="ForumChatThreadCardFooter__counter">
            <LLMetaIconInfo
              :value="thread.meta.responseCount"
              :text="$tc('common_amount_no_digits_responses', thread.meta.responseCount)"
            >
              <TopicRedIcon></TopicRedIcon>
            </LLMetaIconInfo>
            <div v-if="thread.meta.newResponseCount" class="ForumChatThreadCardFooter__responses-new-counter">
              {{ $tc('forum.thread.new_responses_counter', thread.meta.newResponseCount) }}
            </div>
          </div>
          <LLMetaIconInfo
            v-if="thread.hasPoll"
            class="ForumChatThreadCardFooter__counter"
            :value="thread.meta.pollResponseCount"
            :text="$tc('common_amount_no_digits_poll-answers', thread.meta.pollResponseCount)"
          >
            <PollIcon></PollIcon>
          </LLMetaIconInfo>
          <LLMetaIconInfo
            class="ForumChatThreadCardFooter__counter"
            :value="thread.meta.ownResponseCount + thread.meta.ownPollResponseCount"
            grey
            primary-icon
            :text="
              $tc(
                'common_amount_no_digits_responses-by-you',
                thread.meta.ownResponseCount + thread.meta.ownPollResponseCount
              )
            "
          >
            <MessageMyIcon></MessageMyIcon>
          </LLMetaIconInfo>
          <LLMetaIconInfo
            class="ForumChatThreadCardFooter__counter"
            :value="thread.meta.ownRepliesCount"
            grey
            primary-icon
            :text="$tc('common_amount_no_digits_replies-by-you', thread.meta.ownRepliesCount)"
          >
            <MessageReplyIcon></MessageReplyIcon>
          </LLMetaIconInfo>
        </div>
      </div>
      <div
        v-if="(hasLink && hasLinkButton) || isResponseStatusVisibleLocal"
        class="ForumChatThreadCardFooter__grid-link"
      >
        <router-link
          v-if="hasLink && hasLinkButton"
          tag="a"
          :to="link"
          class="ForumChatThreadCardFooter__bottom-link"
        >
          <LLButton small secondary>
            {{ $t('forum.thread.secondary_link') }}
            <template #icon-right>
              <ArrowMiniIcon class="ForumChatThreadCardFooter__bottom-link-arrow" />
            </template>
          </LLButton>
        </router-link>
        <div v-if="isResponseStatusVisibleLocal" class="ForumChatThreadCardFooter__response-status">
          <div
            v-if="!hasRepsonses"
            class="ForumChatThreadCardFooterResponseStatus ForumChatThreadCardFooterResponseStatus_awaiting"
          >
            <WarningFilledIcon class="ForumChatThreadCardFooterResponseStatus__icon" />
            <div class="ForumChatThreadCardFooterResponseStatus__text">
              {{ $t('forum.thread.response_status.awaiting') }}
            </div>
          </div>
          <div
            v-else-if="hasRepsonses && false"
            class="ForumChatThreadCardFooterResponseStatus ForumChatThreadCardFooterResponseStatus_responded"
          >
            <SuccessFilledIcon class="ForumChatThreadCardFooterResponseStatus__icon" />
            <div class="ForumChatThreadCardFooterResponseStatus__text">
              {{ $t('forum.thread.response_status.responded') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ForumThreadModel } from '@/models/forum/forumThread'
import PollIcon from '@/assets/icons/PollIcon.vue'
import TopicRedIcon from '@/assets/icons/TopicRedIcon.vue'
import LLTags from '@/components/common/ui-components/LLTags.vue'
import LLMetaIconInfo from '@/components/atoms/LLMetaIconInfo.vue'
import LLButton from '@/components/common/ui-components/LLButton.vue'
import ArrowMiniIcon from '@/assets/icons/ArrowMiniIcon.vue'
import SuccessFilledIcon from '@/assets/icons/SuccessFilledIcon.vue'
import MessageMyIcon from '@/assets/icons/MessageMyIcon.vue'
import MessageReplyIcon from '@/assets/icons/MessageReplyIcon.vue'
import { ForumThreadState } from '@/common/enums'

export default {
  name: 'ForumChatThreadCardFooter',
  components: {
    MessageReplyIcon,
    MessageMyIcon,
    SuccessFilledIcon,
    ArrowMiniIcon,
    LLButton,
    LLMetaIconInfo,
    LLTags,
    TopicRedIcon,
    PollIcon
  },
  props: {
    thread: { type: ForumThreadModel, required: true },
    hasLink: { type: Boolean, default: true },
    hasLinkButton: { type: Boolean, default: true },
    compact: { type: Boolean, default: false },
    mobile: { type: Boolean, default: false },
    isResponseStatusVisible: { type: Boolean, default: false }
  },
  computed: {
    isResponseStatusVisibleLocal() {
      return this.isResponseStatusVisible && this.thread.state === ForumThreadState.ACTIVE
    },
    link() {
      return `/chats/${this.thread.chatId}/threads/${this.thread.id}`
    },
    classList() {
      return {
        [`${this.$options.name}_mobile`]: this.mobile,
        [`${this.$options.name}_compact`]: this.compact
      }
    },
    hasRepsonses() {
      return !!(this.thread.meta.ownResponseCount + this.thread.meta.ownPollResponseCount)
    }
  }
}
</script>

<style scoped lang="scss">
.ForumChatThreadCardFooter {
  $root: &;
  @apply px-6 w-full;

  &_compact,
  &_mobile {
    @apply px-4;
  }

  &__content {
    @apply flex flex-col w-full;
  }

  &__grid {
    @apply grid gap-4;
    grid-template-columns: 1fr max-content;
  }

  &__counter {
    @apply mt-0 flex flex-nowrap items-center;
    &:first-child {
      @apply mt-0;
    }
  }

  &__grid-tags {
    grid-column: 1;
    grid-row: 1;
  }

  &__grid-link {
    @apply flex items-center flex-row;
    grid-column: 1;
    grid-row: 2;
  }

  &__grid-counters {
    @apply flex flex-col justify-end;
    grid-column: 2;
    grid-row: 1 / 3;
  }

  &__response-status {
    @apply mb-0 ml-4;
    &:first-child {
      @apply ml-0;
    }
  }
  &__responses-new-counter {
    @apply text-accent-01 text-body-02 ml-1;
  }

  #{$root}_compact &__grid,
  #{$root}_mobile &__grid {
    grid-template-columns: 1fr;
  }

  #{$root}_compact &__grid-tags,
  #{$root}_mobile &__grid-tags {
    grid-column: auto;
    grid-row: auto;
  }

  #{$root}_compact &__grid-counters,
  #{$root}_mobile &__grid-counters {
    grid-column: auto;
    grid-row: auto;
  }

  #{$root}_compact &__grid-link,
  #{$root}_mobile &__grid-link {
    @apply flex flex-col-reverse items-start;
    grid-column: auto;
    grid-row: auto;
  }

  #{$root}_compact &__response-status,
  #{$root}_mobile &__response-status {
    @apply mb-4 ml-0;
    &:first-child {
      @apply mb-0;
    }
  }

  &ResponseStatus {
    @apply flex items-center;
    &__icon {
      @apply mr-1 h-4;
    }
    &__text {
      @apply text-body-02;
    }
    &_responded {
      @apply text-success-01;
    }
    &_awaiting {
      @apply text-accent-01-400;
    }
  }
}
</style>
